import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { authAPI, User } from '../services/api.ts';

// Define context types
interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (loginId: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  updateProfile: (updatedUser: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  isAuthenticated: false,
  updateProfile: async () => {},
  changePassword: async () => {},
});

// Create the provider component
export const AuthProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        // Validate token and retrieve user from local storage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('auth_token');
        
        if (storedUser && token) {
          setUser(JSON.parse(storedUser));
          
          // Remove the notification permission request here as it's already handled in App.tsx
          // This prevents duplicate API calls to updateUserToken
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      } finally {
        setLoading(false);
      }
    };

    checkLoggedIn();
  }, []);

  // Login function
  const login = async (loginId: string, password: string) => {
    setLoading(true);
    try {
      // Call the authentication API
      const result = await authAPI.login(loginId, password);
      setUser(result.user);
      
      // Notification permission will be prompted via NotificationPrompt component
      
    } catch (error: any) {
      console.error('Login error:', error);
      // Preserve the error message from the API
      throw new Error(error.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    authAPI.logout();
    setUser(null);
  };

  // Update profile function
  const updateProfile = async (updatedUser: Partial<User>) => {
    setLoading(true);
    try {
      // In a real app, this would be an API call to your backend
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update user with new information
      const newUser = { ...user, ...updatedUser };
      
      // Save updated user to local storage
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
    } catch (error) {
      console.error('Profile update error:', error);
      throw new Error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string) => {
    setLoading(true);
    try {
      // In a real app, this would be an API call to your backend
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // In a real app, you would verify current password with backend
      // For demo, we'll just simulate a successful password change
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In real app, backend would handle the password update
      console.log('Password changed successfully');
      
      // Nothing to update locally since we don't store the password
    } catch (error) {
      console.error('Password change error:', error);
      throw new Error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        updateProfile,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 